"use client";
import { addBreadcrumb, captureMessage } from "@sentry/nextjs";

/**
 * RetryScript is a component that will retry loading a script until the
 * callback returns true. This is for loading third party scripts
 * that we need to ensure are loaded properly on the page.
 *
 * Initially waits 2 seconds to check, then it will retry every 2+0.5(n) seconds
 * indefinitely.
 *
 * @param scriptId
 * @param callback
 * @param onLoad
 * @constructor
 */
const initialWaitTimeout = 1000;
const timeoutIncrement = 500;
const maxRetries = 30; // ~250s total retry time

export const retryScript = (scriptId: string, onLoad?: (e: Event) => void) => {
  const scriptEl = document.querySelector(`script#${scriptId}`) as HTMLScriptElement | null;
  if (!scriptEl) {
    return;
  }

  const retryCount = parseInt(scriptEl.getAttribute("data-retry-script") ?? "0") + 1;
  if (retryCount >= maxRetries) {
    captureMessage("Script failed to load after retrying", {
      level: "error",
      contexts: { scriptInfo: { scriptId, retryCount } },
    });
    return;
  }

  /** Recreate script element, with attributes and onLoad */
  const clone = document.createElement("script");
  for (let i = 0; i < scriptEl.attributes.length; i++) {
    const { name, value } = scriptEl.attributes[i];
    clone.setAttribute(name, value);
  }
  clone.setAttribute("data-retry-script", retryCount.toString());

  clone.addEventListener("load", function (e) {
    if (onLoad) {
      onLoad.call(this, e);
    }
  });
  clone.addEventListener("error", function () {
    retryScript(scriptId, onLoad);
  });

  const waitTime =
    retryCount === 1 ? initialWaitTimeout : initialWaitTimeout + retryCount * timeoutIncrement;
  addBreadcrumb({
    message: `Script failed to load: RetryScript trying again in ${waitTime}ms (${retryCount})`,
    level: "error",
    data: {
      scriptId,
    },
  });

  setTimeout(() => {
    /** Remove old script element and append new one */
    scriptEl.remove();
    document.body.appendChild(clone);
  }, waitTime);
};
